/**
 * The data boundary.
 *
 *   components  ->  ModellerService  ->  live client (default)
 *                                    \-> fixture client (explicit opt-in)
 *
 * Components never import a client and never import a fixture. They take this
 * interface, so switching source is a swap rather than a rewrite — which only
 * holds because the models mirror the API's own field names.
 *
 * PRODUCTION DEFAULTS TO LIVE. Fixture mode is opted into explicitly and
 * REFUSES EVERY WRITE, so a demo record cannot reach the real API by
 * accident.
 */

import type {
  CallSheetExtraction, Confirmation, ConfirmFact, DaySuggestion,
  DocumentSummary, EquipmentClass, Estimate, ExtractionAttempt, NewDocument,
  NewProduction, NewShootDay, Production, ProductionSummary,
  RegisteredDocument, ShootDay,
} from './models';

/** Something the backend cannot answer yet, distinguished from an error.
 *  A screen renders this as "not available", never as a failure — and never
 *  by inventing the number. */
export class NotAvailable extends Error {
  constructor(readonly what: string, readonly why: string) {
    super(`${what} is not available: ${why}`);
    this.name = 'NotAvailable';
  }
}

/** Attempted write while in fixture mode. */
export class FixtureWriteRefused extends Error {
  constructor(op: string) {
    super(
      `fixture mode refuses writes (${op}). Demo data must never reach the ` +
      `production API; switch to the live client to write.`,
    );
    this.name = 'FixtureWriteRefused';
  }
}

export interface ModellerService {
  readonly mode: 'live' | 'fixture';

  productions(): Promise<ProductionSummary[]>;
  production(slug: string): Promise<Production>;
  equipment(): Promise<EquipmentClass[]>;
  estimates(slug: string): Promise<Estimate[]>;
  estimate(id: number): Promise<Estimate>;
  daySuggestions(extractionId: number): Promise<DaySuggestion[]>;

  /* NOTE: there is deliberately no estimateHours(). The profile arrives on
     the estimate itself — `GET /v1/estimates/{id}` returns `hourly` — and a
     method here would imply a second request that does not exist. */

  documents(slug: string): Promise<DocumentSummary[]>;

  // ---- writes. EVERY ONE of these is refused in fixture mode, and every
  // one of them carries the editing key rather than the access key.

  createProduction(body: NewProduction): Promise<Production>;

  /** One operating unit on one local date. The unit is named here, in the
   *  same request — there is no separate unit-creation route, and inventing
   *  one client-side would be a second way to create the same row. */
  createShootDay(slug: string, body: NewShootDay): Promise<ShootDay>;

  /** Record a document's IDENTITY. The bytes are not sent here. */
  registerDocument(slug: string, body: NewDocument): Promise<RegisteredDocument>;

  /** Send the exact registered bytes to be read. The server re-hashes them
   *  and refuses a mismatch before parsing anything. */
  extractCallSheet(
    slug: string, documentId: number, file: File, requestedBy: string,
  ): Promise<CallSheetExtraction>;

  /** Put ONE parsed fact onto ONE unit-day the caller names. Never resolves
   *  a day from a date. */
  confirmFact(factId: number, body: ConfirmFact): Promise<Confirmation>;

  /** Every extraction ATTEMPT against a document, newest first. The document
   *  list's `extraction_count` says how many; this says which, and carries
   *  the ids the review screen navigates to. */
  documentExtractions(slug: string, documentId: number): Promise<ExtractionAttempt[]>;
}
